import { useLayoutEffect, type MouseEvent } from "react";

import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  useFloating,
} from "@floating-ui/react";

import { truncate } from "simplydo/core/helpers";
import { styled } from "simplydo/react/styled";
import { DropdownItem, DropdownMenu } from "simplydo/react/ui/dropdown";
import { H4, Subheader } from "simplydo/react/ui/header";
import { Image } from "simplydo/react/ui/image";
import { Loader } from "simplydo/react/ui/loader";
import { getDefaultMountNode } from "simplydo/react/ui/utils/mountNode";

import type { SuggestionItem } from "./types";

const Avatar = styled(Image)`
  object-fit: cover;
  width: 28px;
  height: 28px;

  &&& {
    margin-right: 10px;
  }
`;

const Emoji = styled.div`
  font-size: 28px;
  align-self: center;
  margin-right: 10px;
`;

const Label = styled.div`
  font-size: 15px;
  font-weight: bold;
`;

const Description = styled.div`
  color: #777;
`;

type DropdownProps = {
  suggestions: Array<SuggestionItem>;
  loading: boolean;
  isActive: boolean;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  createMention: (index: number) => void;
  clientRect: (() => DOMRect | null) | null;
  contextElement: Element | null;
};

const Dropdown = ({
  suggestions,
  loading,
  isActive,
  activeIndex,
  setActiveIndex,
  createMention,
  clientRect,
  contextElement,
}: DropdownProps) => {
  const hasRect = isActive && !!clientRect?.();

  const { refs, floatingStyles, isPositioned } = useFloating({
    open: hasRect,
    placement: "bottom-start",
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      floatingOffset(6),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
    ],
  });

  // TipTap's clientRect is a live getter; wire it as a virtual reference so
  // autoUpdate re-measures on scroll/resize of editor scroll ancestors.
  useLayoutEffect(() => {
    if (!hasRect || !clientRect) {
      refs.setPositionReference(null);
      return;
    }

    refs.setPositionReference({
      getBoundingClientRect: () => clientRect() ?? new DOMRect(),
      contextElement: contextElement ?? undefined,
    });
  }, [hasRect, clientRect, contextElement, refs]);

  if (!hasRect) {
    return null;
  }

  return (
    <FloatingPortal root={getDefaultMountNode() ?? undefined}>
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          zIndex: 999,
          minWidth: 300,
          minHeight: 60,
          overflow: "hidden",
          // Avoid a one-frame flash at 0,0 before Floating UI measures.
          visibility: isPositioned ? "visible" : "hidden",
        }}
      >
        <div
          onMouseDown={(e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <DropdownMenu
            style={{
              width: "100%",
              minHeight: 60,
              maxHeight: 300,
              overflowY: "auto",
              marginTop: 0,
            }}
          >
            {loading ? (
              <DropdownItem>
                <Loader size="small" />
              </DropdownItem>
            ) : null}
            {suggestions.length > 0 || loading ? (
              suggestions.map((item, index) => {
                const isHighlighted = index === activeIndex;
                return (
                  <DropdownItem
                    key={`${item.kind}-${item.id}`}
                    style={isHighlighted ? { background: "#f0f4fa" } : {}}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      createMention(index);
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      {item.kind === "mention" && item.image ? (
                        <Avatar avatar src={item.image} />
                      ) : null}
                      {item.kind === "emoji" ? (
                        <Emoji>{item.emoji}</Emoji>
                      ) : null}
                      <div>
                        <Label>{item.label || ""}</Label>
                        <Description>
                          {truncate(item.description || "", 50)}
                        </Description>
                      </div>
                    </div>
                  </DropdownItem>
                );
              })
            ) : (
              <DropdownItem>
                <H4>
                  Sorry, no results found
                  <Subheader>Start typing or adjust your query.</Subheader>
                </H4>
              </DropdownItem>
            )}
          </DropdownMenu>
        </div>
      </div>
    </FloatingPortal>
  );
};

export default Dropdown;
